import { inject, Injectable, signal } from '@angular/core';
import { Firestore, doc, getDoc, DocumentSnapshot } from '@angular/fire/firestore';
import { ValidarQrResult } from './guardia-qr.service';

const LS_KEY = 'rc_guardia_state';

export interface GuardiaSession {
  uid:               string;
  nombre:            string;
  email:             string;
  residencialId:     string;
  nombreResidencial: string;
}

/**
 * Estado de sesión del guardia.
 *
 * Además del perfil de sesión (igual que ResidenteStateService), gestiona el
 * resultado del último QR escaneado como estado temporal entre páginas:
 *   lnx-guardia-scanner  → escanea QR → llama setQrResult()
 *   lnx-guardia-resultado → lee con consumeQrResult() y limpia el signal
 *
 * Este patrón evita pasar el resultado por query params (expone datos sensibles
 * en la URL) o releer Firestore en la página de resultado.
 */
@Injectable({ providedIn: 'root' })
export class GuardiaStateService {
  private firestore = inject(Firestore);

  private _session       = signal<GuardiaSession | null>(null);
  // Resultado pendiente del último escaneo — solo existe entre scanner y resultado.
  private _pendingResult = signal<{ result: ValidarQrResult; token: string; accion: 'entrada' | 'salida' } | null>(null);

  readonly session       = this._session.asReadonly();
  readonly pendingResult = this._pendingResult.asReadonly();

  constructor() {
    this.hydrateFromStorage();
  }

  /**
   * Misma lógica que ResidenteStateService: lee usuarios/{uid} y residenciales/{id}.
   * Debe llamarse en AuthService.login() antes de navegar al módulo /guardia.
   */
  async loadForGuardia(uid: string, userSnap: DocumentSnapshot): Promise<void> {
    if (!userSnap.exists()) throw new Error('Usuario guardia no encontrado en Firestore');

    const userData = userSnap.data() as {
      nombre:        string;
      email:         string;
      residencialId: string;
    };

    const resSnap = await getDoc(doc(this.firestore, `residenciales/${userData.residencialId}`));
    if (!resSnap.exists()) throw new Error('Residencial no encontrada en Firestore');

    const nombreResidencial = (resSnap.data() as { nombre: string }).nombre;

    const session: GuardiaSession = {
      uid,
      nombre:            userData.nombre,
      email:             userData.email,
      residencialId:     userData.residencialId,
      nombreResidencial
    };

    this._session.set(session);
    localStorage.setItem(LS_KEY, JSON.stringify(session));
  }

  // Llamar desde lnx-guardia-scanner justo antes de navegar a lnx-guardia-resultado.
  setQrResult(result: ValidarQrResult, token: string, accion: 'entrada' | 'salida'): void {
    this._pendingResult.set({ result, token, accion });
  }

  // Llamar desde lnx-guardia-resultado en ngOnInit: lee y limpia en una sola operación
  // para que el signal quede null si el guardia navega atrás y regresa a resultado.
  consumeQrResult(): { result: ValidarQrResult; token: string; accion: 'entrada' | 'salida' } | null {
    const val = this._pendingResult();
    this._pendingResult.set(null);
    return val;
  }

  /**
   * Limpia sesión y resultado pendiente.
   * Debe llamarse en AuthService.logout() antes de signOut().
   */
  clearState(): void {
    this._session.set(null);
    this._pendingResult.set(null);
    localStorage.removeItem(LS_KEY);
  }

  private hydrateFromStorage(): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      this._session.set(JSON.parse(raw) as GuardiaSession);
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }
}
